/* eslint-disable @khan/frontend/require-stories -- we have stories for the Markdown component */
import {StyleSheet} from "aphrodite";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import {addStyle} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {BodyMonospace} from "@khanacademy/wonder-blocks-typography";

import type {MarkdownRule} from "../types.ts";

import {blockStyle} from "./shared-styles.ts";

const StyledPre = addStyle("pre");

// Regex used to match code blocks. It looks for code blocks that are wrapped
// in triple backticks (```) or triple tildes (~~~), optionally followed by a
// language identifier and additional metadata text on the same line.
//   Eg. ```javascript - other meta data if needed
// The code block content is then captured, and the pattern continues to match
// until it finds the closing triple backticks/tildes or reaches the end of
// the string.
// 1. ^: Asserts the start of the string.
// 2. \s*: Matches zero or more whitespace characters.
// 3. (`{3,}|~{3,}): Matches either three or more backticks (```) or three or
//    more tildes (~~~).
// 4.  *: Matches zero or more space characters.
// 5. (\S+)?: Optionally matches one or more non-whitespace characters
//    representsing the coding language.
// 6. ([^\n]*?)?: Optionally matches zero or more characters that are not
//    newline characters representing any additional metadata .
// 7. \n: Matches a newline character.
// 8. ([\s\S]*?): Lazily matches zero or more characters, including whitespace
//    and non-whitespace characters representing the content.
// 9. \s*: Matches zero or more whitespace characters.
// 10. (?:(?:\1 *(?:.*)?(?:\n *)*\n?)|$): This is a non-capturing group that
//     matches either:
//     a. \1: The same character sequence as the first capturing group (either
//        three or more backticks or tildes).
//     b.  *(?:.*)?(?:\n *)*\n?: Optionally matches zero or more space
//        characters, followed by any characters, and then zero or more newline
//        characters with optional spaces.
//     c. |: Or
//     d. $: The end of the string.
const CODE_BLOCK_FENCED_R =
    /^\s*(?<fence>`{3,}|~{3,}) *(?<lang>\S+)?(?<metadata>[^\n]*?)?\n(?<content>[\s\S]*?)\s*(?:(?:\1 *(?:.*)?(?:\n *)*\n?)|$)/;

// Output rules for inline code formatting. Handles text wrapped in backticks `.
// Ex. `this is formatted text`
export const codeRule: Readonly<MarkdownRule> = {
    ...SimpleMarkdown.defaultRules.inlineCode,
    react: (node, output, state) => (
        <BodyMonospace
            key={state.key}
            style={[styles.code, styles.inlineCode]}
            tag="code"
        >
            {node.content}
        </BodyMonospace>
    ),
};

// Output rules for block code formatting. Handles indented lines of the code
// block by at least 4 spaces or 1 tab.
// Ex. The following is block code:
//
//     print("hello world!");
//
// TODO(KORE-510): Override the `match` with a more performant match function.
export const codeBlockRule: Readonly<MarkdownRule> = {
    ...SimpleMarkdown.defaultRules.codeBlock,
    react: (node, output, state) => (
        <StyledPre key={state.key} style={styles.blockCode}>
            <BodyMonospace style={styles.code} tag="code">
                {node.content}
            </BodyMonospace>
        </StyledPre>
    ),
};

// Output rules for block code formatting. Handles wrapped in 3 or more
// backticks (`) or tildes (~) – the closing delimiter is optional if the end
// of the input string is reached.
// Ex. The following is block code:
//
// ```
// print("hello world!");
// ```
//
export const codeBlockFencedRule: Readonly<MarkdownRule> = {
    ...SimpleMarkdown.defaultRules.fence,
    match: SimpleMarkdown.blockRegex(CODE_BLOCK_FENCED_R),
    parse: (capture, parse, state) => {
        return {
            type: "codeBlock",
            // @ts-expect-error - TS2339 - Property 'groups' does not exist on type 'Capture'.
            lang: capture.groups.lang || undefined,
            // @ts-expect-error - TS2339 - Property 'groups' does not exist on type 'Capture'.
            content: capture.groups.content,
        };
    },
};

const styles = StyleSheet.create({
    code: {
        fontSize: "95%",
        color: "inherit",
    },
    inlineCode: {
        display: "inline",
        padding: `${spacing.xxxSmall_4}px ${spacing.xxSmall_6}px`,
        margin: 0,
        backgroundColor: "rgba(175,184,193,0.2)",
        borderRadius: 4,
        whiteSpace: "nowrap",
        color: "inherit",
    },
    blockCode: {
        fontSize: "inherit",
        padding: spacing.medium_16,
        backgroundColor: "rgba(175,184,193,0.2)",
        borderRadius: 4,
        color: "inherit",
        ...blockStyle,
    },
});
